import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Select from 'react-select';
import axios from 'axios';
import { FaSync, FaExclamationTriangle, FaCar, FaUsers } from 'react-icons/fa';
import { useAuth } from './AuthContext';
import { rootUrl } from '../../config/config';

const fmt = (v) => Number(v || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtKes = (v) => `KES ${fmt(v)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

const TYPE_COLOURS = {
    WKI: 'bg-green-100 text-green-700',
    WKR: 'bg-blue-100 text-blue-700',
    EXP: 'bg-red-100 text-red-700',
    ADJ: 'bg-amber-100 text-amber-700',
    WAR: 'bg-purple-100 text-purple-700',
};
const typeBadge = (t) => TYPE_COLOURS[String(t || '').toUpperCase()] || 'bg-slate-100 text-slate-600';

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '34px',
        fontSize: '12px',
        borderColor: state.isFocused ? '#FF5733' : '#e2e8f0',
        boxShadow: state.isFocused ? '0 0 0 1px #FF5733' : 'none',
        '&:hover': { borderColor: '#FF5733' },
    }),
    option: (base, state) => ({
        ...base,
        fontSize: '12px',
        backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#FFF1EB' : '#fff',
        color: state.isSelected ? '#fff' : '#1e293b',
    }),
    menu: (base) => ({ ...base, zIndex: 30 }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    indicatorsContainer: (base) => ({ ...base, height: '34px' }),
};

const weekSelectStyles = {
    ...selectStyles,
    control: (base, state) => ({
        ...selectStyles.control(base, state),
        minHeight: '38px',
        fontSize: '13px',
    }),
    menu: (base) => ({ ...base, zIndex: 50, minWidth: '260px' }),
};

export default function WeeklySummaryPage() {
    const { token } = useAuth();
    const getHeaders = () => ({ Authorization: `Bearer ${token || localStorage.getItem('authToken')}` });

    const [weeks, setWeeks] = useState([]);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [summary, setSummary] = useState([]);
    const [txTypes, setTxTypes] = useState([]);

    const [ownerFilter, setOwnerFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [carFilter, setCarFilter] = useState('');

    const [loading, setLoading] = useState(false);
    const [recalcLoading, setRecalcLoading] = useState(false);
    const [error, setError] = useState('');

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const h = getHeaders();
        axios.get(`${rootUrl}/weekly-summary/weeks`, { headers: h })
            .then(r => {
                const opts = r.data.map(w => ({
                    value: w.week,
                    label: w.week,
                    startDate: w.start_date,
                    endDate: w.end_date,
                }));
                setWeeks(opts);
                if (opts.length > 0) setSelectedWeek(opts[0]);
            })
            .catch(() => {});
        axios.get(`${rootUrl}/weekly-summary/transaction-types`, { headers: h })
            .then(r => setTxTypes(Array.isArray(r.data) ? r.data : []))
            .catch(() => {});
    }, [token]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchSummary = useCallback(async (weekValue) => {
        if (!weekValue) return;
        setLoading(true);
        setError('');
        try {
            const res = await axios.get(`${rootUrl}/weekly-summary/${weekValue}/summary`, { headers: getHeaders() });
            setSummary(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            setError(e.response?.data?.error || e.message || 'Failed to load summary');
            setSummary([]);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (selectedWeek) fetchSummary(selectedWeek.value);
    }, [selectedWeek, fetchSummary]);

    const handleRecalculate = async () => {
        if (!selectedWeek) return;
        setRecalcLoading(true);
        setError('');
        try {
            const res = await axios.post(
                `${rootUrl}/weekly-summary/recalculate?week=${selectedWeek.value}`,
                null,
                { headers: getHeaders() }
            );
            setSummary(Array.isArray(res.data.summary) ? res.data.summary : []);
        } catch (e) {
            setError(e.response?.data?.error || e.message || 'Recalculation failed');
        } finally {
            setRecalcLoading(false);
        }
    };

    // Derived filter options
    const ownerOptions = useMemo(() => {
        const seen = new Map();
        summary.forEach(r => { if (r.owner_code && !seen.has(r.owner_code)) seen.set(r.owner_code, r.owner_name || r.owner_code); });
        return [
            { value: '', label: 'All Owners' },
            ...[...seen.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([code, name]) => ({ value: code, label: `${code} – ${name}` })),
        ];
    }, [summary]);

    const carOptions = useMemo(() => {
        const plates = [...new Set(summary.map(r => r.plate_no).filter(Boolean))].sort();
        return [{ value: '', label: 'All Cars' }, ...plates.map(p => ({ value: p, label: p }))];
    }, [summary]);

    const typeOptions = useMemo(() => [
        { value: '', label: 'All Types' },
        ...txTypes.map(t => ({ value: t.type, label: `${t.type} – ${t.name}` })),
    ], [txTypes]);

    const filtered = useMemo(() => summary.filter(r =>
        (!ownerFilter || r.owner_code === ownerFilter) &&
        (!typeFilter || r.trans_type === typeFilter) &&
        (!carFilter || r.plate_no === carFilter)
    ), [summary, ownerFilter, typeFilter, carFilter]);

    // Aggregate stats (full data, unfiltered)
    const uniqueOwners = useMemo(() => new Set(summary.map(r => r.owner_code).filter(Boolean)).size, [summary]);
    const uniqueCars   = useMemo(() => new Set(summary.map(r => r.plate_no).filter(Boolean)).size, [summary]);
    const totalDebit  = summary.reduce((s, r) => s + Number(r.total_debit  || 0), 0);
    const totalCredit = summary.reduce((s, r) => s + Number(r.total_credit || 0), 0);

    const typeBreakdown = useMemo(() => {
        const map = {};
        summary.forEach(r => {
            if (!map[r.trans_type]) map[r.trans_type] = { type: r.trans_type, net: 0, rows: 0 };
            map[r.trans_type].net   += Number(r.net_amount  || 0);
            map[r.trans_type].rows  += 1;
        });
        return Object.values(map).sort((a, b) => Math.abs(b.net) - Math.abs(a.net));
    }, [summary]);

    // Filtered totals
    const filtDebit  = filtered.reduce((s, r) => s + Number(r.total_debit  || 0), 0);
    const filtCredit = filtered.reduce((s, r) => s + Number(r.total_credit || 0), 0);
    const filtNet    = filtered.reduce((s, r) => s + Number(r.net_amount   || 0), 0);

    const weekDateRange = selectedWeek?.startDate
        ? `${fmtDate(selectedWeek.startDate)} – ${fmtDate(selectedWeek.endDate)}`
        : '';

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="mx-auto max-w-7xl px-4 py-6 pb-10">

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
                    <div>
                        <h1 className="text-2xl font-bold text-[#2a2e30]">Weekly Transactions Summary</h1>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {weekDateRange || 'Aggregated car-level transactions per week'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <div className="w-56">
                            <Select
                                options={weeks}
                                value={selectedWeek}
                                onChange={opt => { setSelectedWeek(opt); setOwnerFilter(''); setTypeFilter(''); setCarFilter(''); }}
                                styles={weekSelectStyles}
                                placeholder="Select week…"
                                isSearchable
                            />
                        </div>
                        <button
                            onClick={handleRecalculate}
                            disabled={recalcLoading || !selectedWeek}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-[#FF5733] text-white hover:bg-[#e64520] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                        >
                            <FaSync className={recalcLoading ? 'animate-spin' : ''} />
                            {recalcLoading ? 'Recalculating…' : 'Recalculate'}
                        </button>
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 text-sm text-red-700">
                        <FaExclamationTriangle className="shrink-0" />
                        <span>{error}</span>
                        <button onClick={() => selectedWeek && fetchSummary(selectedWeek.value)} className="ml-auto text-xs font-semibold underline hover:no-underline">Retry</button>
                    </div>
                )}

                {/* Summary cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <div className="bg-white rounded-xl border border-[#E7D6CA] shadow-sm px-4 py-3 flex items-center gap-3">
                        <FaUsers className="text-2xl text-[#FF5733] shrink-0" />
                        <div>
                            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Owners</p>
                            <p className="text-2xl font-bold text-[#2a2e30]">{loading ? '—' : uniqueOwners}</p>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl border border-[#E7D6CA] shadow-sm px-4 py-3 flex items-center gap-3">
                        <FaCar className="text-2xl text-[#FF5733] shrink-0" />
                        <div>
                            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Cars</p>
                            <p className="text-2xl font-bold text-[#2a2e30]">{loading ? '—' : uniqueCars}</p>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl border border-green-100 shadow-sm px-4 py-3">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Total Debit</p>
                        <p className="text-lg font-bold text-green-700">{loading ? '—' : fmtKes(totalDebit)}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-blue-100 shadow-sm px-4 py-3">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Total Credit</p>
                        <p className="text-lg font-bold text-blue-700">{loading ? '—' : fmtKes(totalCredit)}</p>
                    </div>
                </div>

                {/* Type breakdown grid */}
                {typeBreakdown.length > 0 && (
                    <div className="bg-white rounded-xl border border-[#E7D6CA] shadow-sm mb-4 overflow-hidden">
                        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#F0E6DF]">
                            {/* Income / Reversals */}
                            <div>
                                <div className="px-4 py-2 bg-green-50 border-b border-[#F0E6DF]">
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-green-700">Income &amp; Reversals</span>
                                </div>
                                {typeBreakdown.filter(s => s.net >= 0).length === 0 && (
                                    <p className="px-4 py-3 text-xs text-gray-400">None</p>
                                )}
                                {typeBreakdown.filter(s => s.net >= 0).map(s => {
                                    const typeObj = txTypes.find(t => t.type === s.type);
                                    return (
                                        <div key={s.type} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-50 last:border-0 hover:bg-green-50/40 transition-colors">
                                            <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold ${typeBadge(s.type)}`}>{s.type}</span>
                                            <span className="flex-1 text-xs text-slate-600 leading-tight">{typeObj?.name || s.type}</span>
                                            <span className="text-[10px] text-gray-400 shrink-0">{s.rows} rows</span>
                                            <span className="text-xs font-bold text-green-700 tabular-nums shrink-0 min-w-[90px] text-right">{fmtKes(s.net)}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            {/* Deductions */}
                            <div>
                                <div className="px-4 py-2 bg-red-50 border-b border-[#F0E6DF]">
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-red-600">Deductions &amp; Losses</span>
                                </div>
                                {typeBreakdown.filter(s => s.net < 0).length === 0 && (
                                    <p className="px-4 py-3 text-xs text-gray-400">None</p>
                                )}
                                {typeBreakdown.filter(s => s.net < 0).map(s => {
                                    const typeObj = txTypes.find(t => t.type === s.type);
                                    return (
                                        <div key={s.type} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-50 last:border-0 hover:bg-red-50/40 transition-colors">
                                            <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold ${typeBadge(s.type)}`}>{s.type}</span>
                                            <span className="flex-1 text-xs text-slate-600 leading-tight">{typeObj?.name || s.type}</span>
                                            <span className="text-[10px] text-gray-400 shrink-0">{s.rows} rows</span>
                                            <span className="text-xs font-bold text-red-600 tabular-nums shrink-0 min-w-[90px] text-right">{fmtKes(s.net)}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="bg-white rounded-xl border border-[#E7D6CA] shadow-sm p-4 mb-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                            <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Owner</label>
                            <Select options={ownerOptions} value={ownerOptions.find(o => o.value === ownerFilter) || null}
                                onChange={o => setOwnerFilter(o?.value || '')} styles={selectStyles} placeholder="All Owners" isClearable />
                        </div>
                        <div>
                            <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Type</label>
                            <Select options={typeOptions} value={typeOptions.find(o => o.value === typeFilter) || null}
                                onChange={o => setTypeFilter(o?.value || '')} styles={selectStyles} placeholder="All Types" isClearable />
                        </div>
                        <div>
                            <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Car</label>
                            <Select options={carOptions} value={carOptions.find(o => o.value === carFilter) || null}
                                onChange={o => setCarFilter(o?.value || '')} styles={selectStyles} placeholder="All Cars" isClearable />
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-xl border border-[#E7D6CA] shadow-sm overflow-hidden mb-4">
                    <div className="overflow-x-auto">
                        <table className="min-w-[640px] w-full text-left">
                            <thead>
                                <tr className="border-b border-[#EFE3DB] bg-[#FFF8F5]">
                                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Owner</th>
                                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Car</th>
                                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Type</th>
                                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Debit</th>
                                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Credit</th>
                                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Net</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr><td colSpan="6" className="px-4 py-10 text-center text-sm text-gray-400">
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-4 h-4 border-2 border-[#FF5733] border-t-transparent rounded-full animate-spin" />
                                            Loading…
                                        </div>
                                    </td></tr>
                                )}
                                {!loading && filtered.length === 0 && (
                                    <tr><td colSpan="6" className="px-4 py-10 text-center text-sm text-gray-400">
                                        {summary.length === 0 ? 'No data for this week. Try recalculating.' : 'No rows match the selected filters.'}
                                    </td></tr>
                                )}
                                {!loading && filtered.map((row, i) => {
                                    const typeObj = txTypes.find(t => t.type === row.trans_type);
                                    const net = Number(row.net_amount || 0);
                                    return (
                                        <tr key={`${row.owner_code}-${row.plate_no}-${row.trans_type}-${i}`}
                                            className="border-b border-slate-50 text-xs transition-colors hover:bg-orange-50/50 hover:border-l-2 hover:border-l-[#FF5733]">
                                            <td className="px-4 py-2.5">
                                                <span className="font-semibold text-slate-800">{row.owner_code}</span>
                                                {row.owner_name && row.owner_name !== row.owner_code && (
                                                    <p className="text-[10px] text-gray-400 mt-0.5">{row.owner_name}</p>
                                                )}
                                            </td>
                                            <td className="px-4 py-2.5 font-mono font-bold text-[#FF5733]">{row.plate_no || '—'}</td>
                                            <td className="px-4 py-2.5">
                                                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${typeBadge(row.trans_type)}`}>{row.trans_type}</span>
                                                {typeObj && <p className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[120px]">{typeObj.name}</p>}
                                            </td>
                                            <td className="px-4 py-2.5 text-right font-mono tabular-nums text-slate-700">{fmt(row.total_debit)}</td>
                                            <td className="px-4 py-2.5 text-right font-mono tabular-nums text-slate-700">{fmt(row.total_credit)}</td>
                                            <td className={`px-4 py-2.5 text-right font-mono tabular-nums font-semibold ${net >= 0 ? 'text-green-700' : 'text-red-600'}`}>{fmt(net)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            {!loading && filtered.length > 0 && (
                                <tfoot>
                                    <tr className="bg-[#FFF8F5] border-t-2 border-[#EFE3DB]">
                                        <td colSpan="3" className="px-4 py-2.5 text-xs font-bold text-slate-600 uppercase tracking-wide">
                                            {filtered.length} {filtered.length === 1 ? 'row' : 'rows'}
                                            {(ownerFilter || typeFilter || carFilter) && summary.length !== filtered.length && ` of ${summary.length}`}
                                        </td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums text-sm font-bold text-slate-800">{fmt(filtDebit)}</td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums text-sm font-bold text-slate-800">{fmt(filtCredit)}</td>
                                        <td className={`px-4 py-2.5 text-right font-mono tabular-nums text-sm font-bold ${filtNet >= 0 ? 'text-green-700' : 'text-red-600'}`}>{fmt(filtNet)}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>

            </div>
        </div>
    );
}
